package sarka.design.skoria.messaging

/**
 * Turns message text into the opaque payload the server stores, and back.
 *
 * This exists so end-to-end encryption can be added without touching the send
 * and receive paths. Those paths call [encode] and [decode] today against
 * [PlaintextCodec], which does nothing. Swapping in a real implementation is
 * then a new class plus a version bump, not surgery on the messaging flow.
 *
 * The server never calls this. It treats the payload as bytes.
 */
interface MessageCodec {

    /** Version written into new messages by this codec. */
    val version: Int

    /** True if this codec can read [version]. */
    fun canDecode(version: Int): Boolean

    fun encode(plaintext: String, recipient: String): String

    fun decode(payload: String, version: Int, sender: String): String
}

/** Payload version 0: no encryption, text stored as-is. */
const val PAYLOAD_VERSION_PLAINTEXT = 0

/**
 * The current codec. Readable by the server, which is the tradeoff we chose for
 * v1 so that moderation and support remain possible.
 */
class PlaintextCodec : MessageCodec {

    override val version: Int = PAYLOAD_VERSION_PLAINTEXT

    override fun canDecode(version: Int): Boolean = version == PAYLOAD_VERSION_PLAINTEXT

    override fun encode(plaintext: String, recipient: String): String = plaintext

    override fun decode(payload: String, version: Int, sender: String): String = payload
}

/**
 * Result of decoding, so the UI can distinguish real content from a payload this
 * build can't read.
 *
 * Once a second codec exists, an older client will encounter versions it doesn't
 * understand. Failing loudly per message beats crashing the conversation or
 * silently showing an empty bubble.
 */
sealed class DecodedText {
    data class Text(val value: String) : DecodedText()
    data class Unsupported(val version: Int) : DecodedText()
}

/** Decodes with [codec], or reports the version as unsupported. */
fun MessageCodec.decodeOrUnsupported(
    payload: String,
    version: Int,
    sender: String,
): DecodedText = if (canDecode(version)) {
    DecodedText.Text(decode(payload, version, sender))
} else {
    DecodedText.Unsupported(version)
}

/** Text to show for a payload this build can't decode. */
fun DecodedText.displayText(): String = when (this) {
    is DecodedText.Text -> value
    is DecodedText.Unsupported -> "Message not supported by this app version"
}
